import { apiGet, apiPost, apiPatch } from '@/utils/api';

// Types
export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string | null;
  pricingType: 'PER_USER' | 'FIXED_FEE' | 'HYBRID';
  basePrice: number;
  pricePerUser: number | null;
  billingCycle: 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
  maxUsers: number | null;
  maxCampuses: number | null;
  features: Record<string, boolean>;
  tvaRate: number;
  tvaExempt: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Subscription {
  id: string;
  tenantId: string;
  tenant?: {
    id: string;
    name: string;
    ice?: string;
    ifiscal?: string;
    rc?: string;
  };
  planId: string;
  plan?: SubscriptionPlan;
  status: 'TRIAL' | 'ACTIVE' | 'PAST_DUE' | 'CANCELLED' | 'SUSPENDED';
  startDate: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  trialEndsAt: string | null;
  cancelledAt: string | null;
  userCount: number;
  nextPaymentDate: string;
  lastPaymentDate: string | null;
  // Business info
  businessName?: string | null;
  ice?: string | null;
  ifiscal?: string | null;
  rc?: string | null;
  billingAddress?: string | null;
  billingCity?: string | null;
  // User exclusion settings
  excludeStaff: boolean;
  excludeProfessors: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface InvoiceLineItem {
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
  isReduction?: boolean;
  motif?: string;
}

export interface SubscriptionInvoice {
  id: string;
  subscriptionId: string;
  subscription?: Subscription;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  subtotal: number;
  tvaAmount: number;
  total: number;
  tvaRate: number;
  status: 'DRAFT' | 'SENT' | 'PAID' | 'OVERDUE' | 'CANCELLED';
  paidAt: string | null;
  lineItems: InvoiceLineItem[];
  pdfUrl: string | null;
  createdAt: string;
  // Versioning
  periodYear?: number;
  periodMonth?: number;
  version?: number;
  isLatest?: boolean;
  previousInvoiceId?: string | null;
}

export interface CreatePlanDto {
  name: string;
  description?: string;
  pricingType: 'PER_USER' | 'FIXED_FEE' | 'HYBRID';
  basePrice: number;
  pricePerUser?: number;
  billingCycle: 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
  maxUsers?: number;
  maxCampuses?: number;
  features?: Record<string, boolean>;
  tvaRate?: number;
  tvaExempt?: boolean;
}

export interface CreateSubscriptionDto {
  tenantId: string;
  planId: string;
  startDate?: string;
  trialDays?: number;
}

// API Functions - Plans
export const getPlans = async (): Promise<SubscriptionPlan[]> => {
  return apiGet<SubscriptionPlan[]>('/billing/plans');
};

export const getPlan = async (id: string): Promise<SubscriptionPlan> => {
  return apiGet<SubscriptionPlan>(`/billing/plans/${id}`);
};

export const createPlan = async (data: CreatePlanDto): Promise<SubscriptionPlan> => {
  return apiPost<SubscriptionPlan>('/billing/plans', data);
};

export const updatePlan = async (id: string, data: Partial<CreatePlanDto>): Promise<SubscriptionPlan> => {
  return apiPatch<SubscriptionPlan>(`/billing/plans/${id}`, data);
};

// Paginated response type
interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// API Functions - Subscriptions
export const getSubscriptions = async (status?: string): Promise<Subscription[]> => {
  const query = status ? `?status=${status}` : '';
  const response = await apiGet<PaginatedResponse<Subscription> | Subscription[]>(`/billing/subscriptions${query}`);
  // Handle both paginated and array responses
  return Array.isArray(response) ? response : response.data;
};

export const getSubscription = async (tenantId: string): Promise<Subscription> => {
  return apiGet<Subscription>(`/billing/subscriptions/${tenantId}`);
};

export const createSubscription = async (data: CreateSubscriptionDto): Promise<Subscription> => {
  return apiPost<Subscription>('/billing/subscriptions', data);
};

export const updateSubscription = async (tenantId: string, data: Partial<{
  planId: string;
  status: string;
  userCount: number;
  businessName: string;
  ice: string;
  ifiscal: string;
  rc: string;
  billingAddress: string;
  billingCity: string;
  excludeStaff: boolean;
  excludeProfessors: boolean;
}>): Promise<Subscription> => {
  return apiPatch<Subscription>(`/billing/subscriptions/${tenantId}`, data);
};

export const cancelSubscription = async (tenantId: string, reason?: string): Promise<Subscription> => {
  return apiPost<Subscription>(`/billing/subscriptions/${tenantId}/cancel`, { reason });
};

export const reactivateSubscription = async (tenantId: string): Promise<Subscription> => {
  return apiPost<Subscription>(`/billing/subscriptions/${tenantId}/reactivate`, {});
};

// API Functions - Invoices
export const getInvoices = async (filters?: {
  subscriptionId?: string;
  status?: string;
}): Promise<SubscriptionInvoice[]> => {
  const params = new URLSearchParams();
  if (filters?.subscriptionId) params.append('subscriptionId', filters.subscriptionId);
  if (filters?.status) params.append('status', filters.status);
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await apiGet<PaginatedResponse<SubscriptionInvoice> | SubscriptionInvoice[]>(`/billing/invoices${query}`);
  // Handle both paginated and array responses
  return Array.isArray(response) ? response : response.data;
};

export const getInvoice = async (id: string): Promise<SubscriptionInvoice> => {
  return apiGet<SubscriptionInvoice>(`/billing/invoices/${id}`);
};

export interface InvoiceAdjustment {
  description: string;
  amount: number;
  motif?: string;
}

export const generateInvoice = async (
  subscriptionId: string,
  options?: {
    forceNewVersion?: boolean;
    adjustments?: InvoiceAdjustment[];
  }
): Promise<SubscriptionInvoice> => {
  return apiPost<SubscriptionInvoice>(`/billing/invoices/generate/${subscriptionId}`, options || {});
};

export const updateInvoiceAdjustments = async (
  invoiceId: string,
  adjustments: InvoiceAdjustment[]
): Promise<SubscriptionInvoice> => {
  return apiPatch<SubscriptionInvoice>(`/billing/invoices/${invoiceId}/adjustments`, { adjustments });
};

export const sendInvoice = async (id: string): Promise<SubscriptionInvoice> => {
  return apiPost<SubscriptionInvoice>(`/billing/invoices/${id}/send`, {});
};

export const markInvoicePaid = async (id: string, paidAt?: string): Promise<SubscriptionInvoice> => {
  return apiPost<SubscriptionInvoice>(`/billing/invoices/${id}/pay`, { paidAt });
};

// Get invoice version history
export const getInvoiceVersions = async (invoiceId: string): Promise<SubscriptionInvoice[]> => {
  return apiGet<SubscriptionInvoice[]>(`/billing/invoices/${invoiceId}/versions`);
};

// Get cumulative totals for a subscription (institution)
export const getCumulativeTotal = async (tenantId: string): Promise<{
  subscriptionId: string;
  totalPaid: number;
  totalOutstanding: number;
  invoiceCount: number;
  paidCount: number;
}> => {
  return apiGet<{
    subscriptionId: string;
    totalPaid: number;
    totalOutstanding: number;
    invoiceCount: number;
    paidCount: number;
  }>(`/billing/subscriptions/${tenantId}/cumulative`);
};

// Download invoice PDF
export const downloadInvoicePdf = async (id: string, invoiceNumber: string): Promise<void> => {
  const token = localStorage.getItem('nexedu_access_token');
  const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api/billing/invoices/${id}/pdf`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    // Try to get error details from response
    let errorMessage = 'Erreur lors du téléchargement de la facture';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
      console.error('PDF download error:', errorData);
    } catch {
      // Response wasn't JSON, use default message
      console.error('PDF download failed with status:', response.status);
    }
    throw new Error(errorMessage);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${invoiceNumber}.pdf`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

// Helpers
export const formatCurrency = (amount: number | string | null | undefined): string => {
  // Handle null/undefined
  if (amount == null) return '0,00 MAD';

  // Parse string to number (Prisma Decimal comes as string)
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  // Handle NaN
  if (isNaN(numAmount)) return '0,00 MAD';

  return new Intl.NumberFormat('fr-MA', {
    style: 'currency',
    currency: 'MAD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numAmount);
};

// Helper to safely parse amounts (for calculations)
export const parseAmount = (amount: number | string | null | undefined): number => {
  if (amount == null) return 0;
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return isNaN(num) ? 0 : num;
};

export const getStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    ACTIVE: 'bg-green-100 text-green-800',
    TRIAL: 'bg-blue-100 text-blue-800',
    PAST_DUE: 'bg-yellow-100 text-yellow-800',
    CANCELLED: 'bg-gray-100 text-gray-800',
    SUSPENDED: 'bg-red-100 text-red-800',
    DRAFT: 'bg-gray-100 text-gray-800',
    SENT: 'bg-blue-100 text-blue-800',
    PAID: 'bg-green-100 text-green-800',
    OVERDUE: 'bg-red-100 text-red-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

export const getStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    ACTIVE: 'Actif',
    TRIAL: 'Essai',
    PAST_DUE: 'En retard',
    CANCELLED: 'Annulé',
    SUSPENDED: 'Suspendu',
    DRAFT: 'Brouillon',
    SENT: 'Envoyée',
    PAID: 'Payée',
    OVERDUE: 'En retard',
    PER_USER: 'Par utilisateur',
    FIXED_FEE: 'Forfait fixe',
    HYBRID: 'Forfait fixe + par utilisateur',
    MONTHLY: 'Mensuel',
    QUARTERLY: 'Trimestriel',
    YEARLY: 'Annuel',
  };
  return labels[status] || status;
};

// Invoice Statistics Types
export interface InvoiceStatistics {
  total: number;
  byStatus: {
    DRAFT: number;
    SENT: number;
    PAID: number;
    OVERDUE: number;
    CANCELLED: number;
  };
  byMonth: Array<{
    year: number;
    month: number;
    total: number;
    draft: number;
    sent: number;
    paid: number;
    overdue: number;
    totalAmount: number;
    paidAmount: number;
  }>;
  totals: {
    totalAmount: number;
    paidAmount: number;
    pendingAmount: number;
    overdueAmount: number;
  };
}

// Get Invoice Statistics (KPIs)
export const getInvoiceStatistics = async (): Promise<InvoiceStatistics> => {
  return apiGet<InvoiceStatistics>('/billing/invoices/statistics');
};
